<template>
  <modal name="brand-configuration" class="modal-inner modal-brand-configuration" transition="pop-out" :width="modalWidth" :focus-trap="true" :min-height="500" height="auto" :scrollable="true">
    <span class="close-button" @click="hide()"><BaseIcon icon="times-circle" class="text-white" /></span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      <span>{{ $t("brands_page.form_title") }}</span>
    </div>

    <div class="modal-body brand-form">
      <div class="flex flex-wrap items-start">
      <!-- ============================ form ============================ -->
      <div class="w-full lg:w-2/3 px-3 pb-4">
        <div class="form-intro">
          <h2 class="form-intro__title">{{ $t("brands_page.form_title") }}</h2>
          <p class="form-intro__text">{{ $t("brands_page.form_subtitle") }}</p>
        </div>

        <ValidationObserver ref="formBranding">
          <!-- 1 general -->
          <section class="section">
            <div class="section__head">
              <div>
                <h2 class="section__title">{{ $t("brands_page.section_general") }}</h2>
                <p class="section__hint">{{ $t("brands_page.section_general_hint") }}</p>
              </div>
              <span class="section__step">{{ $t("brands_page.step", { n: 1 }) }}</span>
            </div>
            <div class="flex flex-wrap -mx-2">
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="SecurCloud" v-model="form.productName" type="text" :field_name="$t('brands_page.product_name')" />
                <p class="field-help">{{ $t("brands_page.product_name_help") }}</p>
              </div>
              <div class="w-full md:w-1/2 px-2 pb-4">
                <BaseInput placeholder="https://csg.securcloud.se" v-model="form.portalUrl" type="text" rules="required|http_url" :field_name="$t('brands_page.portal_url')" />
                <p class="field-help">{{ $t("brands_page.portal_url_help") }}</p>
                <p v-if="portalUrlProblem" class="field-help field-help--bad">{{ $t("brands_page." + portalUrlProblem) }}</p>
              </div>
              <div class="w-full md:w-1/2 px-2 pb-4">
                <BaseTextarea placeholder="csg.securcloud.se" rows="2" v-model="form.hosts" rules="required" :field_name="$t('brands_page.hosts')" />
                <p class="field-help">{{ $t("brands_page.hosts_help") }}</p>
              </div>
              <div class="w-full md:w-1/2 px-2 pb-4">
                <BaseInput placeholder="G-XXXXXXXXXX" v-model="form.analyticsId" type="text" :field_name="$t('brands_page.analytics_id')" />
                <p class="field-help">{{ $t("brands_page.analytics_id_help") }}</p>
              </div>
              <!-- SEC-334: languages of this brand, decided by its own web addresses -->
              <div class="w-full md:w-1/2 px-2 pb-4">
                <BaseSelect v-model="form.portalLanguage" :options="portalLanguageOptions" nameSelector="name" optionSelector="code" :field_name="$t('brands_page.portal_language')" />
                <p class="field-help">{{ $t("brands_page.portal_language_help") }}</p>
              </div>
              <div class="w-full md:w-1/2 px-2 pb-4">
                <BaseSelect v-model="form.emailLanguage" :options="emailLanguageOptions" nameSelector="name" optionSelector="code" :field_name="$t('brands_page.email_language')" />
                <p class="field-help">{{ $t("brands_page.email_language_help") }}</p>
              </div>
            </div>
          </section>

          <!-- 2 logos -->
          <section class="section">
            <div class="section__head">
              <div>
                <h2 class="section__title">{{ $t("brands_page.section_logos") }}</h2>
                <p class="section__hint">{{ $t("brands_page.section_logos_hint") }} {{ $t("brands_page.logo_formats") }}</p>
              </div>
              <span class="section__step">{{ $t("brands_page.step", { n: 2 }) }}</span>
            </div>
            <div class="flex flex-wrap -mx-2">
              <div v-for="logo in logoTypes" :key="logo.type" class="w-full md:w-1/3 px-2 pb-4">
                <div class="logo-card">
                  <div class="logo-card__head">
                    <span class="logo-card__title">{{ $t("brands_page.logo_" + logo.type) }}</span>
                    <span class="logo-card__badge">{{ $t("brands_page.logo_badge_" + logo.type) }}</span>
                  </div>
                  <div class="logo-card__body" :class="{ 'logo-card__body--dark': logo.dark }">
                    <img v-if="logoSrc(logo.type)" :src="logoSrc(logo.type)" />
                    <span v-else class="logo-card__empty">
                      <BaseIcon icon="cloud-upload-alt" class="block mx-auto mb-1" />
                      {{ $t("brands_page.no_file") }}
                    </span>
                  </div>
                  <label class="logo-card__action">
                    {{ logoSrc(logo.type) ? $t("brands_page.replace_file") : $t("brands_page.choose_file") }}
                    <input type="file" class="hidden" accept="image/png,image/jpeg,image/svg+xml" @change="onLogoChange(logo.type, $event)" />
                  </label>
                </div>
              </div>
            </div>
          </section>

          <!-- 3 colours -->
          <section class="section">
            <div class="section__head">
              <div>
                <h2 class="section__title">{{ $t("brands_page.section_colors") }}</h2>
                <p class="section__hint">{{ $t("brands_page.section_colors_hint") }}</p>
              </div>
              <span class="section__step">{{ $t("brands_page.step", { n: 3 }) }}</span>
            </div>
            <div class="flex flex-wrap -mx-1">
              <div v-for="shade in shades" :key="shade" class="w-1/2 md:w-1/5 px-1 pb-3">
                <label class="swatch__shade">{{ shade }}</label>
                <div class="swatch">
                  <input type="color" v-model="form.colors.primary[shade]" class="swatch__dot" />
                  <input type="text" v-model="form.colors.primary[shade]" class="swatch__hex" />
                </div>
                <!-- what this shade paints, so nobody has to guess which box matters -->
                <p class="swatch__use">{{ $t("brands_page.shade_use_" + shade) }}</p>
              </div>
            </div>
            <p v-if="lowContrast" class="field-help field-help--bad">{{ $t("brands_page.contrast_warning") }}</p>
            <div class="flex flex-wrap -mx-2 pt-2">
              <div class="w-full md:w-1/3 px-2 pb-2">
                <label class="field-label">{{ $t("brands_page.accent") }}</label>
                <div class="swatch">
                  <input type="color" v-model="form.colors.accent" class="swatch__dot" />
                  <input type="text" v-model="form.colors.accent" class="swatch__hex" />
                </div>
              </div>
              <div class="w-full md:w-1/3 px-2 pb-2">
                <BaseInput placeholder="rgba(37, 41, 44, 0.6)" v-model="form.colors.overlay" type="text" :field_name="$t('brands_page.overlay')" />
                <p class="field-help">{{ $t("brands_page.overlay_help") }}</p>
              </div>
            </div>
          </section>

          <!-- 4 typography -->
          <section class="section">
            <div class="section__head">
              <div>
                <h2 class="section__title">{{ $t("brands_page.section_typography") }}</h2>
                <p class="section__hint">{{ $t("brands_page.section_typography_hint") }}</p>
              </div>
              <span class="section__step">{{ $t("brands_page.step", { n: 4 }) }}</span>
            </div>
            <div class="flex flex-wrap -mx-2">
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="Source Serif Pro" v-model="form.typography.headingFont" type="text" :field_name="$t('brands_page.heading_font')" />
              </div>
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="Source Sans Pro" v-model="form.typography.textFont" type="text" :field_name="$t('brands_page.text_font')" />
              </div>
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="https://fonts.googleapis.com/css2?family=Raleway" v-model="form.typography.stylesheetUrl" type="text" :field_name="$t('brands_page.stylesheet_url')" />
                <p class="field-help">{{ $t("brands_page.stylesheet_url_help") }}</p>
              </div>
            </div>
          </section>

          <!-- 5 contact -->
          <section class="section">
            <div class="section__head">
              <div>
                <h2 class="section__title">{{ $t("brands_page.section_contact") }}</h2>
                <p class="section__hint">{{ $t("brands_page.section_contact_hint") }}</p>
              </div>
              <span class="section__step">{{ $t("brands_page.step", { n: 5 }) }}</span>
            </div>
            <div class="flex flex-wrap -mx-2">
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="Commuter Security Group AB" v-model="form.contact.companyName" type="text" rules="required" :field_name="$t('brands_page.company_name')" />
              </div>
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="556000-0000" v-model="form.contact.companyNumber" type="text" :field_name="$t('brands_page.company_number')" />
              </div>
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseTextarea placeholder="Finlandsgatan 60" rows="2" v-model="form.contact.address" :field_name="$t('brands_page.address')" />
              </div>
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="08-410 147 00" v-model="form.contact.supportPhone" type="text" rules="required" :field_name="$t('brands_page.support_phone')" />
              </div>
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="support@csgab.se" v-model="form.contact.supportEmail" type="text" rules="required|email" :field_name="$t('brands_page.support_email')" />
              </div>
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="https://csgab.se" v-model="form.contact.websiteUrl" type="text" rules="required|http_url" :field_name="$t('brands_page.website_url')" />
              </div>
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="csgab.se" v-model="form.contact.websiteLabel" type="text" :field_name="$t('brands_page.website_label')" />
                <p class="field-help">{{ $t("brands_page.website_label_help") }}</p>
              </div>
            </div>
          </section>

          <!-- 6 e-mail -->
          <section class="section">
            <div class="section__head">
              <div>
                <h2 class="section__title">{{ $t("brands_page.section_email") }}</h2>
                <p class="section__hint">{{ $t("brands_page.section_email_hint") }}</p>
              </div>
              <span class="section__step">{{ $t("brands_page.step", { n: 6 }) }}</span>
            </div>
            <div class="flex flex-wrap -mx-2">
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="noreply@csg.securcloud.se" v-model="form.email.sender" type="text" rules="email" :field_name="$t('brands_page.email_sender')" />
                <p class="field-help">{{ $t("brands_page.email_sender_help") }}</p>
              </div>
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="order@csgab.se" v-model="form.email.orders" type="text" rules="required|email" :field_name="$t('brands_page.email_orders')" />
                <p class="field-help">{{ $t("brands_page.email_orders_help") }}</p>
              </div>
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="support@csgab.se" v-model="form.email.errorReports" type="text" rules="required|email" :field_name="$t('brands_page.email_error_reports')" />
                <p class="field-help">{{ $t("brands_page.email_error_reports_help") }}</p>
              </div>
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="order@csgab.se" v-model="form.email.notifications" type="text" rules="required|email" :field_name="$t('brands_page.email_notifications')" />
                <p class="field-help">{{ $t("brands_page.email_notifications_help") }}</p>
              </div>
              <div class="w-full md:w-1/3 px-2 pb-4">
                <BaseInput placeholder="support@securdia.se" v-model="form.email.copy" type="text" rules="email" :field_name="$t('brands_page.email_copy')" />
                <p class="field-help">{{ $t("brands_page.email_copy_help") }}</p>
              </div>
            </div>

            <div class="w-full pt-2">
              <h3 class="section__title">{{ $t("brands_page.section_readiness") }}</h3>
              <p class="section__hint pb-3">{{ $t("brands_page.readiness_note") }}</p>
              <label v-for="item in readinessItems" :key="item.key" class="check-row">
                <input v-model="form.readiness[item.key]" type="checkbox" class="form-checkbox h-4 w-4" />
                <span class="ml-3 text-sm text-gray-700">{{ $t("brands_page.readiness_" + item.label) }}</span>
              </label>
            </div>
          </section>
        </ValidationObserver>
      </div>

      <!-- ============================ preview ============================ -->
      <div class="w-full lg:w-1/3 px-3 pb-4">
        <div class="rail">
          <div class="rail__head">
            <span class="rail__dot"></span>
            {{ $t("brands_page.preview_interactive") }}
          </div>

          <Tabs class="rail__tabs">
            <Tab :title="$t('brands_page.preview_tab_login')">
              <div class="browser">
                <div class="browser__bar">
                  <span class="browser__dot" style="background:#ff5f57"></span>
                  <span class="browser__dot" style="background:#febc2e"></span>
                  <span class="browser__dot" style="background:#28c840"></span>
                  <span class="browser__url">{{ previewUrl }}</span>
                </div>
                <div class="browser__screen" :style="{ background: form.colors.overlay }">
                  <div class="signin" :style="{ fontFamily: textFontCss }">
                    <img v-if="logoSrc('horizontal')" :src="logoSrc('horizontal')" class="signin__logo" />
                    <div v-else class="signin__logo-placeholder">
                      <BaseIcon icon="image" class="block mx-auto mb-1" />
                      {{ $t("brands_page.preview_logo_placeholder") }}
                    </div>
                    <label class="signin__label">{{ $t("brands_page.preview_email") }}</label>
                    <input type="text" readonly value="user@example.com" class="signin__input" :style="{ borderColor: form.colors.accent }" />
                    <label class="signin__label">{{ $t("brands_page.preview_password") }}</label>
                    <input type="password" readonly value="password" class="signin__input" />
                    <button type="button" class="signin__button" :style="{ backgroundColor: form.colors.primary[500] }">{{ $t("brands_page.preview_signin") }}</button>
                    <a href="#" @click.prevent class="signin__forgot" :style="{ color: form.colors.primary[500] }">{{ $t("brands_page.preview_forgot") }}</a>
                  </div>
                </div>
              </div>
            </Tab>

            <Tab :title="$t('brands_page.preview_tab_navigation')">
              <div class="sidebar-preview" :style="{ fontFamily: textFontCss }">
                <img v-if="logoSrc('menu')" :src="logoSrc('menu')" class="sidebar-preview__logo" />
                <div class="sidebar-preview__item" :style="{ backgroundColor: form.colors.primary[700] }">{{ $t("brands_page.preview_link") }}</div>
                <div class="sidebar-preview__item sidebar-preview__item--plain">{{ $t("brands_page.preview_link") }}</div>
                <div class="sidebar-preview__foot">
                  <div v-if="form.contact.supportPhone" class="flex items-center pb-1">
                    <img v-if="logoSrc('favicon')" :src="logoSrc('favicon')" class="w-3 mr-2" />
                    {{ form.contact.supportPhone }}
                  </div>
                  <div>{{ form.contact.websiteLabel || form.contact.websiteUrl }}</div>
                </div>
              </div>
            </Tab>

            <Tab :title="$t('brands_page.preview_tab_typography')">
              <div class="type-preview">
                <div class="type-preview__heading" :style="{ fontFamily: headingFontCss, color: form.colors.primary[700] }">{{ $t("brands_page.preview_heading") }}</div>
                <p class="type-preview__body" :style="{ fontFamily: textFontCss }">{{ $t("brands_page.preview_text") }}</p>
              </div>
            </Tab>

            <Tab :title="$t('brands_page.preview_tab_mail')">
              <div class="mail">
                <div class="mail__head">
                  <div><span class="mail__field">{{ $t("brands_page.preview_mail_from") }}:</span> {{ $t("brands_page.preview_mail_from_value") }}</div>
                  <div><span class="mail__field">{{ $t("brands_page.preview_mail_subject_label") }}:</span> {{ $t("brands_page.preview_mail_subject") }}</div>
                </div>

                <div class="mail__body">
                  <p>{{ $t("brands_page.preview_mail_greeting") }}</p>
                  <p>{{ $t("brands_page.preview_mail_body") }}</p>
                  <span class="mail__button" :style="{ color: form.colors.primary[500], borderColor: form.colors.primary[500] }">
                    {{ $t("brands_page.preview_mail_button") }}
                  </span>

                  <div class="mail-preview">
                    <p class="mail-preview__regards">{{ $t("brands_page.preview_regards") }}</p>
                    <div class="mail-preview__signature">
                      <div class="mail-preview__logo-cell">
                        <img v-if="logoSrc('email')" :src="logoSrc('email')" />
                        <span v-else class="mail-preview__logo-empty">{{ $t("brands_page.preview_logo_placeholder") }}</span>
                      </div>
                      <div class="mail-preview__details">
                        <h3 class="mail-preview__company">
                          <span>Support</span><span v-if="form.contact.companyName" class="mail-preview__sep"> | {{ form.contact.companyName }}</span>
                        </h3>
                        <p class="mail-preview__lines">
                          <template v-for="(line, i) in addressLines"><span :key="i">{{ line }}<br /></span></template>
                          <template v-if="form.contact.supportPhone">&#9742; {{ form.contact.supportPhone }}<br /></template>
                          <template v-if="form.contact.supportEmail">&#9993; {{ form.contact.supportEmail }}<br /></template>
                          <a v-if="form.contact.websiteUrl" href="#" @click.prevent>{{ form.contact.websiteUrl }}</a>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Tab>

            <Tab :title="$t('brands_page.preview_tab_pdf')">
              <div class="pdf">
                <div class="pdf__head">
                  <img v-if="logoSrc('pdf')" :src="logoSrc('pdf')" class="pdf__logo" />
                  <span v-else class="pdf__logo-empty">{{ $t("brands_page.preview_logo_placeholder") }}</span>
                  <div class="pdf__title">{{ $t("alarm_pdf.title") }}</div>
                </div>
                <div class="pdf__section">1. {{ $t("alarm_pdf.supplier") }}</div>
                <table class="pdf__table">
                  <thead>
                    <tr>
                      <th>{{ $t("alarm_pdf.name") }}</th>
                      <th>{{ $t("alarm_pdf.org_number") }}</th>
                      <th>{{ $t("alarm_pdf.address") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ form.contact.companyName }}</td>
                      <td>{{ form.contact.companyNumber }}</td>
                      <td>{{ addressLines.join(", ") }}</td>
                    </tr>
                  </tbody>
                </table>
                <table class="pdf__table">
                  <thead>
                    <tr>
                      <th>{{ $t("alarm_pdf.contact_company") }}</th>
                      <th>{{ $t("alarm_pdf.e_mail") }}</th>
                      <th>{{ $t("alarm_pdf.phone") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ form.contact.companyName }}</td>
                      <td>{{ form.contact.supportEmail }}</td>
                      <td>{{ form.contact.supportPhone }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Tab>

          </Tabs>

          <p class="rail__foot">{{ $t("brands_page.preview_synced") }}</p>
        </div>
      </div>
      </div>
    </div>

    <div class="modal-footer">
      <div class="footer-container clearfix">
        <button class="btn-red-outline px-3 py-2 float-left" @click="hide()">
          {{ $t("brands_page.cancel") }}<BaseIcon icon="times-circle" class="ml-1" />
        </button>
        <button class="btn-green px-3 py-2 float-right" @click="apply()">
          {{ $t("brands_page.apply") }}<BaseIcon icon="check-circle" class="ml-1" />
        </button>
      </div>
    </div>
  </modal>
</template>
<script>
import { SECURDIA_COLORS, SECURDIA_TYPOGRAPHY } from "@/constants/brands";
import supportedLocales from "@/config/supported-locales";

// SEC-334: the portal has screens in all five; the API has mail texts in these four only
const EMAIL_LANGUAGES = ["sv", "no", "nl", "en"];
import { extend } from "vee-validate";

// The API refuses a portal or website address without http:// or https://, so the form
// refuses it first, with a message that says what to type.
extend("http_url", {
  validate: (value) => /^https?:\/\/[^\s/]+/i.test(String(value || "").trim()),
  message: (field) => `${field}: start with http:// or https://`,
});
import Tabs from "@/components/base/_tabs";
import Tab from "@/components/base/_tab";

const MODAL_WIDTH = 1280;
const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
const PREVIEW_FONT_LINK_ID = "brand-preview-fonts";

// Relative luminance and contrast ratio (WCAG), for the preview warning
function luminance(hex) {
  const clean = String(hex || "").replace("#", "");
  if (clean.length !== 6) return 1;
  const channels = [0, 2, 4].map((i) => {
    const c = parseInt(clean.substring(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}
function contrastWithWhite(hex) {
  return 1.05 / (luminance(hex) + 0.05);
}

// Only the keys that hold a value, so a stored "" cannot override a default
function withoutEmpty(object) {
  const kept = {};
  for (const [key, value] of Object.entries(object || {})) {
    if (value !== "" && value !== null && value !== undefined) kept[key] = value;
  }
  return kept;
}

function emptyForm() {
  return {
    productName: "SecurCloud",
    hosts: "", // one per line in the form, an array in the API
    portalUrl: "",
    analyticsId: "",
    portalLanguage: "", // empty = the existing country rule
    emailLanguage: "",  // empty = the existing rule
    colors: { primary: { ...SECURDIA_COLORS.primary }, accent: SECURDIA_COLORS.accent, overlay: SECURDIA_COLORS.overlay },
    typography: { ...SECURDIA_TYPOGRAPHY },
    contact: { companyName: "", companyNumber: "", address: "", supportPhone: "", supportEmail: "", websiteUrl: "", websiteLabel: "" },
    email: { sender: "", orders: "", errorReports: "", notifications: "", copy: "" },
    readiness: { dns: false, webServer: false, mapsKey: false },
    logos: { horizontal: "", menu: "", favicon: "", pdf: "", email: "" },
  };
}

// SEC-334 white label: the brand configuration of a reseller, opened from the Branding
// button on the reseller form once White-label is set to Yes. It only collects values and
// hands them back; the reseller form saves them with the reseller, which is what lets it
// work while the reseller is still being created and has no id yet.
export default {
  name: "BrandConfiguration",
  components: { Tabs, Tab },
  data() {
    return {
      modalWidth: MODAL_WIDTH,
      form: emptyForm(),
      logoFiles: {},    // type -> File chosen in this session
      logoPreviews: {}, // type -> object URL of the chosen file
      saving: false,
      shades: SHADES,
      logoTypes: [
        { type: "horizontal", dark: false },
        { type: "menu", dark: true },
        { type: "favicon", dark: true },
        { type: "pdf", dark: false },
        { type: "email", dark: false },
      ],
      readinessItems: [
        { key: "dns", label: "dns" },
        { key: "webServer", label: "web_server" },
        { key: "mapsKey", label: "maps_key" },
      ],
    };
  },
  computed: {
    // SEC-334: names as the language switcher shows them
    portalLanguageOptions() {
      return Object.keys(supportedLocales).map((code) => ({ code, name: supportedLocales[code] }));
    },
    emailLanguageOptions() {
      return EMAIL_LANGUAGES.map((code) => ({ code, name: supportedLocales[code] }));
    },
    headingFontCss() {
      return `'${this.form.typography.headingFont || "Source Serif Pro"}', serif`;
    },
    textFontCss() {
      return `'${this.form.typography.textFont || "Source Sans Pro"}', sans-serif`;
    },
    addressLines() {
      return String(this.form.contact.address || "").split("\n").filter((line) => line.trim() !== "");
    },
    // The address shown in the preview's address bar. Only the scheme is stood in for,
    // so an untouched form still looks like a browser rather than showing partner data
    // that was never entered.
    previewUrl() {
      const url = String(this.form.portalUrl || "").replace(/\/+$/, "");
      return (url || "https://…") + "/login";
    },
    // The portal address is what puts this brand's domain on the API's allowed list.
    // Empty or malformed means every call from that domain is refused and the portal
    // falls back to Securdia, so both cases are called out while the admin types.
    portalUrlProblem() {
      const hosts = String(this.form.hosts || "").trim();
      const url = String(this.form.portalUrl || "").trim();

      if (hosts !== "" && url === "") return "portal_url_missing";
      if (url === "") return "";
      if (!/^https?:\/\//.test(url)) return "portal_url_scheme";
      if (url.endsWith("/")) return "portal_url_slash";
      return "";
    },
    // Buttons use primary 500 and 700 with white text
    lowContrast() {
      return contrastWithWhite(this.form.colors.primary[500]) < 4.5 || contrastWithWhite(this.form.colors.primary[700]) < 4.5;
    },
  },
  watch: {
    "form.typography.stylesheetUrl"(url) {
      this.loadPreviewFonts(url);
    },
  },
  methods: {
    // Opened from the reseller form with whatever has been entered so far. The order and
    // fault-report addresses live on the reseller document itself and come in separately.
    open(branding, reseller, logoFiles, logoPreviews) {
      this.logoFiles = { ...(logoFiles || {}) };
      this.logoPreviews = { ...(logoPreviews || {}) };
      this.fill(branding || {});
      this.form.email.orders = (reseller && reseller.alternativ_order_email) || "";
      this.form.email.errorReports = (reseller && reseller.alternativ_error_email) || "";
      this.$modal.show("brand-configuration");
    },
    hide() {
      this.$modal.hide("brand-configuration");
    },
    // Hand the values back to the reseller form, which saves them with the reseller
    async apply() {
      const valid = await this.$refs.formBranding.validate();
      if (!valid) return;

      this.$emit("apply", {
        branding: this.payload(),
        reseller: {
          alternativ_order_email: this.form.email.orders,
          alternativ_error_email: this.form.email.errorReports,
        },
        logoFiles: this.logoFiles,
        logoPreviews: this.logoPreviews,
      });
      this.hide();
    },
    // Fill the form from a stored branding object
    fill(record) {
      this.form = emptyForm();
      if (!record) return;

      this.form.productName = record.productName || "SecurCloud";
      this.form.hosts = (record.hosts || []).join("\n");
      this.form.portalUrl = record.portalUrl || "";
      this.form.analyticsId = record.analyticsId || "";
      this.form.portalLanguage = record.portalLanguage || "";
      this.form.emailLanguage = record.emailLanguage || "";
      // A reseller stores every branding field from the start, empty until filled in. An
      // empty colour must never replace the Securdia default: the API refuses a colour that
      // is not a hex value, so a form opened from such a record could never be saved.
      if (record.colors) {
        this.form.colors.primary = { ...SECURDIA_COLORS.primary, ...withoutEmpty(record.colors.primary) };
        this.form.colors.accent = record.colors.accent || SECURDIA_COLORS.accent;
        this.form.colors.overlay = record.colors.overlay || SECURDIA_COLORS.overlay;
      }
      this.form.typography = { ...SECURDIA_TYPOGRAPHY, ...withoutEmpty(record.typography) };
      this.form.contact = { ...this.form.contact, ...(record.contact || {}) };
      this.form.email = { ...this.form.email, ...(record.email || {}) };
      this.form.readiness = { ...this.form.readiness, ...(record.readiness || {}) };
      this.form.logos = { ...this.form.logos, ...(record.logos || {}) };
      this.loadPreviewFonts(this.form.typography.stylesheetUrl);
    },
    logoSrc(type) {
      return this.logoPreviews[type] || this.form.logos[type] || "";
    },
    onLogoChange(type, event) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      this.$set(this.logoFiles, type, file);
      this.$set(this.logoPreviews, type, URL.createObjectURL(file));
    },
    // The preview loads the chosen font stylesheet; the real portal does the same in utils/brand.js
    loadPreviewFonts(url) {
      let link = document.getElementById(PREVIEW_FONT_LINK_ID);
      if (!url || !/^https:\/\//.test(url)) {
        if (link) link.remove();
        return;
      }
      if (!link) {
        link = document.createElement("link");
        link.id = PREVIEW_FONT_LINK_ID;
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }
      link.href = url;
    },
    payload() {
      const hosts = String(this.form.hosts || "")
        .split("\n")
        .map((h) => h.trim().toLowerCase())
        .filter((h) => h !== "");
      return {
        productName: this.form.productName,
        hosts,
        portalUrl: this.form.portalUrl,
        analyticsId: this.form.analyticsId,
        portalLanguage: this.form.portalLanguage,
        emailLanguage: this.form.emailLanguage,
        colors: this.form.colors,
        typography: this.form.typography,
        contact: this.form.contact,
        email: this.form.email,
        readiness: this.form.readiness,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.form-intro {
  padding: 16px 0 4px;

  &__title {
    margin: 0;
    font-family: 'Source Serif Pro', serif;
    font-size: 20px;
    font-weight: 700;
    color: #1f2937;
  }

  &__text {
    margin: 6px 0 0;
    font-size: 13px;
    line-height: 1.55;
    color: #6b7280;
    max-width: 60ch;
  }
}

// ---- sections
.section {
  padding: 26px 0;
  border-bottom: 1px solid #e5e7eb;

  &:last-child {
    border-bottom: none;
  }

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &__title {
    margin: 0;
    font-family: 'Source Serif Pro', serif;
    font-size: 15px;
    font-weight: 700;
    color: #1f2937;
  }

  &__hint {
    margin: 3px 0 0;
    font-size: 12px;
    color: #6b7280;
  }

  &__step {
    flex: none;
    margin-left: 16px;
    font-size: 11px;
    color: #9ca3af;
    white-space: nowrap;
  }
}

// same as the label of BaseInput, so the hand-written labels and the
// component ones read alike (text-gray-600 text-sm font-bold pb-2 font-serif)
.field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 700;
  color: #4b5563;
  font-family: 'Source Serif Pro', serif;
  margin-bottom: 8px;
}

.field-help {
  margin: 6px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: #4b5563;

  &--bad {
    color: #dc2626;
    font-weight: 600;
  }
}

// ---- logo cards
.logo-card {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  overflow: hidden;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    border-bottom: 1px solid #f3f4f6;
  }

  &__title {
    font-size: 11px;
    font-weight: 700;
    color: #374151;
  }

  &__badge {
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: #6b7280;
    background: #f3f4f6;
    border-radius: 3px;
    padding: 2px 6px;
    white-space: nowrap;
  }

  &__body {
    height: 84px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;

    img {
      max-height: 60px;
      max-width: 90%;
    }

    &--dark {
      background: #25292c;
    }
  }

  &__empty {
    font-size: 10px;
    color: #9ca3af;
    text-align: center;
  }

  &__action {
    display: block;
    padding: 7px 10px;
    border-top: 1px solid #f3f4f6;
    font-size: 11px;
    font-weight: 600;
    color: #1a8de9;
    cursor: pointer;

    &:hover {
      background: #f9fafb;
    }
  }
}

// ---- colour scale
.swatch {
  display: flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 5px;
  padding: 4px 6px;
  background: #fff;

  &__shade {
    display: block;
    font-size: 10px;
    color: #9ca3af;
    margin-bottom: 4px;
  }

  &__use {
    margin: 4px 0 0;
    font-size: 10px;
    line-height: 13px;
    color: #9ca3af;
  }

  &__dot {
    width: 20px;
    height: 20px;
    flex: none;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
  }

  &__hex {
    width: 100%;
    min-width: 0;
    margin-left: 6px;
    border: none;
    background: none;
    font-size: 11px;
    color: #374151;
    text-transform: uppercase;

    &:focus {
      outline: none;
    }
  }
}

.check-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  cursor: pointer;
}

// ---- preview rail
.rail {
  position: sticky;
  top: 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fbfbfc;
  padding: 14px;

  &__head {
    display: flex;
    align-items: center;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #6b7280;
    margin-bottom: 12px;
  }

  &__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #22c55e;
    margin-right: 7px;
  }

  &__foot {
    margin: 12px 0 0;
    font-size: 10px;
    color: #9ca3af;
  }

  &__tabs {
    ::v-deep .tabs {
      display: flex;
      background: #f3f4f6;
      border-radius: 6px;
      padding: 3px;
      margin-bottom: 12px;
    }

    ::v-deep .tab {
      flex: 1;
      text-align: center;
      padding: 5px 4px;
      font-size: 11px;
      color: #6b7280;
      border-radius: 4px;
      cursor: pointer;

      &.active {
        background: #fff;
        color: #111827;
        font-weight: 600;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
      }
    }
  }
}

// ---- browser mock
.browser {
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  overflow: hidden;
  background: #fff;

  &__bar {
    display: flex;
    align-items: center;
    padding: 7px 9px;
    background: #f3f4f6;
    border-bottom: 1px solid #e5e7eb;
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 4px;
    flex: none;
  }

  &__url {
    flex: 1;
    margin-left: 8px;
    padding: 2px 8px;
    background: #fff;
    border-radius: 10px;
    font-size: 9px;
    color: #6b7280;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__screen {
    padding: 22px 16px;
  }
}

.signin {
  background: #fff;
  border-radius: 7px;
  padding: 16px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);

  &__logo {
    max-height: 28px;
    margin: 0 auto 12px;
    display: block;
  }

  &__name {
    text-align: center;
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 12px;
  }

  &__label {
    display: block;
    font-size: 9px;
    color: #6b7280;
    margin-bottom: 3px;
  }

  &__input {
    width: 100%;
    margin-bottom: 9px;
    padding: 5px 7px;
    font-size: 11px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-bottom-width: 2px;
    border-radius: 4px;
  }

  &__button {
    width: 100%;
    padding: 7px;
    font-size: 12px;
    color: #fff;
    border-radius: 5px;
  }

  &__forgot {
    display: block;
    margin-top: 9px;
    font-size: 10px;
    text-align: center;
  }
}

.sidebar-preview {
  background: #25292c;
  border-radius: 7px;
  padding: 14px;

  &__logo {
    max-height: 26px;
    margin: 0 auto 14px;
    display: block;
  }

  &__item {
    padding: 6px 9px;
    border-radius: 4px;
    font-size: 11px;
    color: #fff;
    margin-bottom: 5px;

    &--plain {
      background: transparent;
      color: #d1d5db;
    }
  }

  &__foot {
    margin-top: 12px;
    padding-top: 9px;
    border-top: 1px solid #374151;
    font-size: 10px;
    color: #e5e7eb;
  }
}

.type-preview,
.mail-preview {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  padding: 14px;
}

.type-preview {
  &__heading {
    font-size: 18px;
    margin-bottom: 6px;
  }

  &__body {
    font-size: 12px;
    color: #4b5563;
    margin: 0;
  }
}

// Mirrors the footer the API puts on a partner brand's mails
// (utils/email-translations.js, getBrandFooterSignature)
.mail-preview {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 11px;
  line-height: 1.5;
  color: #333;

  &__regards {
    font-weight: bold;
    margin: 0 0 12px;
  }

  &__signature {
    display: flex;
    align-items: center;
  }

  &__logo-cell {
    flex: none;
    padding-right: 10px;
    margin-right: 10px;
    border-right: 1px solid #eee;

    img {
      max-width: 96px;
      display: block;
    }
  }

  &__company {
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    line-height: 18px;
    color: rgb(61, 133, 198);
  }

  &__sep {
    color: rgb(51, 51, 51);
  }

  &__lines {
    margin: 2px 0 0;
    font-size: 11px;
    line-height: 16px;
    color: rgb(51, 51, 51);

    a {
      color: rgb(51, 51, 51);
    }
  }
}

// A whole mail: the header a client shows, the body, then the real footer
.mail {
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  overflow: hidden;
  background: #fff;

  &__head {
    padding: 8px 12px;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    font-size: 10px;
    color: #4b5563;
    line-height: 1.7;
  }

  &__field {
    color: #9ca3af;
  }

  &__body {
    padding: 14px 12px;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 11px;
    line-height: 1.6;
    color: #333;

    p {
      margin: 0 0 10px;
    }
  }

  &__button {
    display: inline-block;
    margin: 4px 0 16px;
    padding: 5px 15px;
    font-size: 11px;
    font-weight: 500;
    background: #fff;
    border: 1px solid;
    border-radius: 5px;
  }
}

// The alarm plan PDF: logo and title, then the supplier tables
.pdf {
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
  padding: 14px 12px;

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &__logo {
    max-height: 26px;
    max-width: 45%;
  }

  &__logo-empty {
    padding: 6px 10px;
    border: 1px dashed #d1d5db;
    border-radius: 3px;
    font-size: 9px;
    color: #9ca3af;
  }

  &__title {
    text-align: right;
    font-size: 10px;
    line-height: 1.4;
    color: #374151;
    white-space: pre-line;
  }

  &__section {
    font-size: 10px;
    font-weight: 700;
    color: #374151;
    margin-bottom: 5px;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 12px;
    font-size: 9px;

    th {
      background: #3eb2de;
      color: #fff;
      font-weight: 700;
      text-align: left;
      padding: 4px 5px;
    }

    td {
      border: 1px solid #e5e7eb;
      padding: 4px 5px;
      color: #374151;
      vertical-align: top;
    }
  }
}

.mail-preview__logo-empty {
  display: inline-block;
  padding: 6px 8px;
  border: 1px dashed #d1d5db;
  border-radius: 3px;
  font-size: 9px;
  color: #9ca3af;
}

.signin__logo-placeholder {
  height: 34px;
  margin-bottom: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed #d1d5db;
  border-radius: 4px;
  font-size: 9px;
  color: #9ca3af;
}
</style>

